import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import ProductService from "../../Services/products_service";
import Footer from "../Footer/Footer";
import "./ProductPage.css";
import { getFileUrl } from "../utils/fileUrl";

const alphabet = [..."ABCDEFGHIJKLMNOPQRSTUVWXYZ"];

// Helper function to convert numbers to Unicode subscript characters
const toSubscript = (str) => {
  if (!str) return str;
  const subscriptMap = {
    "0": "₀",
    "1": "₁",
    "2": "₂",
    "3": "₃",
    "4": "₄",
    "5": "₅",
    "6": "₆",
    "7": "₇",
    "8": "₈",
    "9": "₉",
  };
  return str.replace(/[0-9]/g, (digit) => subscriptMap[digit] || digit);
};

const Products = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedLetter, setSelectedLetter] = useState(null);
  const [categoryList, setCategoryList] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryDescription, setCategoryDescription] = useState("");
  const [selectedCategoryName, setSelectedCategoryName] = useState("");
  const [totalAPI, setTotalAPI] = useState(0);
  const [letterAPI, setLetterAPI] = useState(0);
  const [searchQuery, setSearchQuery] = useState(""); // State for search input

  const query = new URLSearchParams(location.search).get("query") || "a";
  const selectedCategoryRef = React.useRef(null);

  // Fetch total product count
  useEffect(() => {
    const fetchTotalCount = async () => {
      try {
        const response = await ProductService.getProductCount();
        if (response.success) {
          setTotalAPI(response.count || 0);
        } else {
          console.error("Error fetching total API count:", response.message);
        }
      } catch (error) {
        console.error("Error fetching total API count:", error);
      }
    };
    fetchTotalCount();
  }, []);

  // Fetch products by slug
  const fetchProductsBySlug = async (slug) => {
  setLoading(true);
  setProducts([]);

  const minDelay = new Promise((resolve) => setTimeout(resolve, 500)); // 1 second delay

  try {
    const response = await ProductService.fetchProductsBySlug(slug);
    const data = Array.isArray(response.data) ? response.data : [response.data];
    
    await minDelay; // ensures loading spinner shows at least 1s

    if (response.success) {
      setProducts(data);
    } else {
      console.error("Error fetching products:", response.message);
      setProducts([]);
    }
  } catch (err) {
    console.error("Error fetching product by slug:", err);
    await minDelay;
    setProducts([]);
  } finally {
    setLoading(false);
  }
};


  // Fetch categories and initialize based on query
  useEffect(() => {
    const initializeFromQuery = async () => {
      setSelectedLetter(null);
      setProducts([]);
      setSelectedCategory(null);
      setCategoryList([]);
      setCategoryDescription("");
      setSelectedCategoryName("");

      const firstLetter = query[0].toUpperCase();
      setSelectedLetter(firstLetter);
      setLoading(true);

      try {
        const fetchedCategories = await ProductService.getCategoriesByAlpabet(
          firstLetter.toLowerCase()
        );
        setCategoryList(fetchedCategories || []);
        setLetterAPI(fetchedCategories.length || 0);

        const match = fetchedCategories.find(
          (cat) =>
            cat.slug?.toLowerCase() === query.toLowerCase() ||
            cat.categorySlug?.toLowerCase() === query.toLowerCase()
        );

        if (match) {
          setSelectedCategory(query);
          setCategoryDescription(match.description || "");
          setSelectedCategoryName(match.name || "");
          fetchProductsBySlug(query);
        } else {
          fetchProductsBySlug(query);
        }
      } catch (err) {
        console.error("Error initializing from query", err);
      } finally {
        setLoading(false);
      }
    };

    initializeFromQuery();
  }, [query]);

  // Handle letter click
  const handleLetterClick = async (letter) => {
    setSelectedLetter(letter);
    setSelectedCategory(null);
    setCategoryDescription("");
    setSelectedCategoryName("");
    setProducts([]);
    setSearchQuery(""); // Clear search query
    setLoading(true);

    navigate(`/products?query=${letter.toLowerCase()}`, { replace: true });

    try {
      const fetchedCategories = await ProductService.getCategoriesByAlpabet(
        letter.toLowerCase()
      );
      setCategoryList(fetchedCategories || []);
      setLetterAPI(fetchedCategories.length || 0);
    } catch {
      setCategoryList([]);
      setLetterAPI(0);
    } finally {
      setLoading(false);
    }
  };
  

  // Handle category click
  const handleCategoryClick = (slug) => {
    setSelectedCategory(slug);
    setSearchQuery("");
    fetchProductsBySlug(slug);

    // Update URL without remounting
    window.history.pushState({}, "", `/products?query=${slug}`);

    const selected = categoryList.find(
      (cat) => cat.slug === slug || cat.categorySlug === slug
    );
    setCategoryDescription(selected?.description || "");
    setSelectedCategoryName(selected?.name || "");
  };

  // Handle search input change
  const handleSearchInputChange = async (event) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (query.trim() === "") {
      setProducts([]);
      return;
    }

    setLoading(true);
    try {
      const response = await ProductService.searchProducts(query);
      if (response.success) {
        setProducts(response.data || []);
      } else {
        console.error("Search error:", response.message);
        setProducts([]);
      }
    } catch (err) {
      console.error("Search error:", err);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Auto-scroll to selected category (super smooth)
  useEffect(() => {
    if (selectedCategoryRef.current) {
      selectedCategoryRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center", // keep in middle for nice UX
      });
    }
  }, [selectedCategory]);

  

  return (
    <div className="min-h-screen flex flex-col">
      <div className="products-page flex-grow">
        {/* Total APIs Info */}
        <div className="api-count-bar">
          {selectedLetter && (
            <p>
              TOTAL APIS FOR "<strong>{selectedLetter}</strong>":{" "}
              <strong>{letterAPI}</strong>
            </p>
          )}
        </div>

        {/* Alphabet Buttons */}
        <div className="alphabet-buttons">
          {alphabet.map((letter) => (
            <button
              key={letter}
              onClick={() => handleLetterClick(letter)}
              className={`letter-button ${
                selectedLetter === letter ? "active" : ""
              }`}
            >
              {letter}
            </button>
          ))}
        </div>

        {/* Category Section */}
        <div className="category-section">
          {selectedCategoryName ? (
            <div className="selected-category-layout">
              <div className="category-description-area">
                <div className="breadcrumb">
                  <Link to="/products?query=a">Home</Link> /{" "}
                  <strong className="current-item">
                    {selectedCategoryName}
                  </strong>
                </div>
                {categoryDescription && (
                  <div className="category-description">
                    {categoryDescription
                      .split(/\n\s*\n/)
                      .map((para, idx) => (
                        <p key={idx} style={{ marginBottom: "1.5em" }}>
                          {para}
                        </p>
                      ))}
                  </div>
                )}
                {selectedCategoryName && (
                  <h3
                    style={{
                      fontSize: "30px",
                      color: "#004c75",
                      fontWeight: "600",
                      marginTop: "10px",
                    }}
                  >
                    {selectedCategoryName} Related Products
                  </h3>
                )}
              </div>
              <div className="sidebar-categories">
                <h3 className="category-title sticky top-0 bg-gray-50 p-2 mb-1">
                  Categories
                </h3>
                <div>
                  {categoryList.map((category, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleCategoryClick(category.slug)}
                      className={`category-button w-full text-left ${
                        selectedCategory === category.slug ? "active" : ""
                      }`}
                      ref={
                        selectedCategory === category.slug
                          ? selectedCategoryRef
                          : null
                      }
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="category-buttons">
              {categoryList.map((category, idx) => (
                <button
                  key={idx}
                  onClick={() => handleCategoryClick(category.slug)}
                  className={`category-button ${
                    selectedCategory === category.slug ? "active" : ""
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          )}
        </div>

       {/* Product List */}
<div className="product-list">
  {loading ? (
    <div className="loading-wrapper">
      <div className="dual-ring"></div>
      <p style={{ marginTop: "10px", color: "#004c75" }}>Loading products...</p>
    </div>
  ) : products.length > 0 ? (
    products.map((product, index) => (
      <Link
        to={`/product-detail/${product.catalogNumber}`}
        className={`product-card12 show`}
        key={index}
      >
        <h3 className="product_name">{product.name}</h3>
      {product.image ? (
  <img
    src={getFileUrl(product.image)}
    alt={product.name}
    className="product-image12"
    onError={(e) => {
      e.currentTarget.style.display = "none";
    }}
  />
) : (
  <div className="no-image-placeholder">
    No Image 
  </div>
)}


        <div className="product-details text-sm space-y-2">
          <div className="grid grid-cols-[130px_10px_1fr] items-start">
            <span className="font-semibold">Catalog No</span>
            <span>:</span>
            <span className="break-words whitespace-pre-wrap">
              {product.catalogNumber}
            </span>
          </div>
          <div className="grid grid-cols-[130px_10px_1fr] items-start">
            <span className="font-semibold">CAS No</span>
            <span>:</span>
            <span className="break-words whitespace-pre-wrap">
              {product.casNumber}
            </span>
          </div>
          <div className="grid grid-cols-[130px_10px_1fr] items-start">
            <span className="font-semibold">Mol.F</span>
            <span>:</span>
            <span className="break-words whitespace-pre-wrap">
              {toSubscript(product.molecularFormula)}
            </span>
          </div>
          <div className="grid grid-cols-[130px_10px_1fr] items-start">
            <span className="font-semibold">Mol.W</span>
            <span>:</span>
            <span className="break-words whitespace-pre-wrap">
              {product.molecularWeight}
            </span>
          </div>
        </div>
      </Link>
    ))
  ) : null}
</div>


      </div>
      <Footer />
    </div>
  );
};
  
export default Products;
